'use client';
import { useState } from 'react';
import type { Route } from './+types/home';
import { Link } from 'react-router';
import { LeafMark } from '~/components/marketing/leaf-mark';
import { ResponsiveImage } from '~/components/ui/responsive-image';
import { SITE, PRICING, calcPrice, formatPrice } from '~/data/site';
import { useBranding, brandingFromMatches } from '~/lib/branding';
import { QuickLeadForm } from '~/components/marketing/quick-lead-form';
import { ReviewMarquee } from '~/components/marketing/review-marquee';
import { buildMeta, schemaFor } from '~/lib/seo';

// The crew grid this page used to render — live employee headshots pulled
// cross-origin from the platform's /api/public/crew — was removed 2026-09-10
// along with its clientLoader. Vikelas asked for the names and photos to come
// off the site, so the fetch had nothing left to feed.

// NOTE: only the tab title/OG identity is fixed here. The hero's "So I built
// New Leaf" founder quote and the Chapter I/II narrative further down are
// New Leaf's own founding story, not a swappable brand string — flagged back
// rather than half-genericized (see session report).
export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return buildMeta({
    title: branding.isNewLeaf
      ? 'New Leaf Cleaning — Transparent Pricing, Greater Tampa Bay'
      : `${branding.name} — Transparent Pricing`,
    description:
      'See your exact price before you call. W-2 cleaning technicians, same crew every visit, per-sqft pricing published on this page. Greater Tampa Bay.',
    path: '/',
    siteName: branding.name,
    origin: branding.origin,
    jsonLd: schemaFor('/', branding.isNewLeaf),
  });
};

type ServiceKey = 'weekly' | 'biweekly' | 'every4' | 'maintenance' | 'fulldeep' | 'moveout';

const SERVICES: { key: ServiceKey; label: string; rate: number; recurring: boolean }[] = [
  { key: 'weekly',      label: 'Weekly',           rate: PRICING.weeklyRate,      recurring: true },
  { key: 'biweekly',    label: 'Biweekly',         rate: PRICING.biweeklyRate,    recurring: true },
  { key: 'every4',      label: 'Every 4 weeks',    rate: PRICING.monthly4Rate,    recurring: true },
  { key: 'maintenance', label: 'One-Time Maintenance', rate: PRICING.maintenanceRate, recurring: false },
  { key: 'fulldeep',    label: 'Full Deep Clean',     rate: PRICING.fullDeepRate,    recurring: false },
  { key: 'moveout',     label: 'Move-In / Out',       rate: PRICING.moveOutRate,     recurring: false },
];

function HeroCalculator() {
  const [sqft, setSqft] = useState(2400);
  const [selected, setSelected] = useState<ServiceKey>('biweekly');

  const service = SERVICES.find((s) => s.key === selected)!;
  const price = sqft > 0 ? calcPrice(sqft, service.key) : 0;
  const atFloor = sqft > 0 && sqft * service.rate < PRICING.minimum;

  return (
    <div className="hero-calc">
      <div className="hero-calc-row">
        <span className="hero-calc-label">My home is</span>
        <input
          type="number"
          className="calc-input"
          value={sqft || ''}
          min={100}
          max={10000}
          onChange={(e) => setSqft(Math.max(0, parseInt(e.target.value) || 0))}
          aria-label="Square footage"
        />
        <span className="hero-calc-label">sq ft</span>
      </div>
      <div className="calc-services">
        {SERVICES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`calc-btn${selected === s.key ? ' active' : ''}`}
            onClick={() => setSelected(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sqft > 0 && (
        <div className="hero-calc-result">
          <span className="hero-calc-price">{formatPrice(price)}</span>
          <span className="hero-calc-sub">
            {sqft.toLocaleString()} sqft × ${service.rate.toFixed(2)}
            {service.recurring ? ' per visit' : ' flat rate'}
            {atFloor ? ` — ${PRICING.minimum} minimum` : ''}
          </span>
        </div>
      )}
      {/* Lead capture — name + phone → Book My Clean */}
      <div className="hero-lead-form">
        <QuickLeadForm source="hero-calculator" label="Book My Clean →" />
      </div>
    </div>
  );
}

export default function HomePage() {
  const branding = useBranding();
  const exampleSqft = 2400;
  const examplePrice = calcPrice(exampleSqft, 'biweekly');

  return (
    <main id="main-content">
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="hero hero-centered">
        {/* Real client kitchen photo — subtle background */}
        <ResponsiveImage
          src="/images/hero-bg.jpg"
          alt=""
          width={1920}
          height={1080}
          priority
          className="hero-bg-img"
        />
        <div className="container hero-center-inner">

          {/* LEFT — copy */}
          <div className="hero-copy-col">
            <p className="eyebrow hero-eyebrow-text">
              Residential cleaning · Greater Tampa Bay
            </p>
            <h1 className="display-xl hero-heading">
              Breathing life back into your home,{' '}
              <em className="hero-em">one clean at a time</em>.
            </h1>
            <p className="lede hero-lede-centered">
              The same trusted team, every visit &mdash; W&#8209;2, background screened, fully
              insured. Pricing published right here, because you shouldn&rsquo;t have to
              call to get a number.
            </p>

            <div className="hero-signature">
              <div className="hero-sig-mono">VC</div>
              <div className="hero-sig-text">
                &ldquo;I called five cleaning companies. None of them showed me the price
                before I called. So I built New Leaf.&rdquo;
                <strong>Vikelas · Founder</strong>
              </div>
            </div>
          </div>

          {/* RIGHT — calculator */}
          <div className="hero-calc-col">
            <HeroCalculator />
          </div>

        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────── */}
      <section className="trust-strip">
        <div className="container">
          <div className="trust-badges">
            <div className="trust-badge">
              <svg className="trust-badge-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              <span className="trust-badge-label">W-2 Employees Only</span>
            </div>
            <div className="trust-badge">
              <svg className="trust-badge-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              <span className="trust-badge-label">Background Screened</span>
            </div>
            <div className="trust-badge">
              <svg className="trust-badge-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              <span className="trust-badge-label">Fully Insured &amp; Bonded</span>
            </div>
            <div className="trust-badge">
              <svg className="trust-badge-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="none" aria-hidden="true"><path d="m12 2.5 2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9Z" /></svg>
              <span className="trust-badge-label">5.0 Stars on Yelp</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROOF, UP FRONT ──────────────────────────────────
          Reviews used to be Chapter V, three quarters of the way down. They
          are the strongest thing on the page, so they now run directly under
          the hero as a scrolling band. New Leaf only — see ReviewMarquee. */}
      {branding.isNewLeaf && <ReviewMarquee />}

      {/* ── CHAPTER I: THE PROBLEM ───────────────────────── */}
      <section className="chapter chapter-alt">
        <div className="chapter-inner container">
          <div className="chapter-body">
            <span className="chapter-label">Chapter one · the problem</span>
            <h2 className="display-lg">
              Most cleaning companies send a{' '}
              <em className="italic-display">different stranger</em> every visit.
            </h2>
            <p className="lede chapter-lede">
              The crew changes, the standard slips, and you spend Saturday morning resetting
              expectations. When a different cleaner shows up, the trust you built
              doesn&rsquo;t transfer. You&rsquo;re back to a baseline wariness.
            </p>
            <p className="marginalia chapter-marginalia">
              That&rsquo;s the only problem we built New Leaf to fix.
            </p>
          </div>
        </div>
      </section>

      {/* ── CHAPTER II: THE PROMISE ──────────────────────── */}
      <section className="chapter">
        <div className="chapter-inner container">
          <div className="chapter-body-wide">
            <span className="chapter-label">Chapter two · the promise</span>
            <h2 className="display-lg">
              Same crew. Same standard.{' '}
              <em className="italic-display">Every time.</em>
            </h2>
            <p className="lede chapter-lede">
              Three commitments we don&rsquo;t compromise on. Not for scale. Not for margin.
            </p>
          </div>

          <div className="leaf-cards">
            <article className="leaf-card">
              <div className="leaf-card-icon">
                <LeafMark size={24} fillColor="#B08D57" />
              </div>
              <h3>The same faces, visit after visit.</h3>
              <p>
                Your crew is assigned. You&rsquo;ll know their names before the second visit.
                When someone can&rsquo;t make it, we tell you &mdash; we don&rsquo;t quietly
                swap in a stranger. They&rsquo;ll treat your home like you&rsquo;re watching,
                even when you&rsquo;re not.
              </p>
            </article>
            <article className="leaf-card">
              <div className="leaf-card-icon">
                <LeafMark size={24} fillColor="#B08D57" />
              </div>
              <h3>W-2 employees. Every one.</h3>
              <p>
                Paid employees, not contractors. Insured, bonded, background-checked.
                Better jobs attract better people, and better people stay &mdash; which is
                why your crew is consistent and your home is covered.
              </p>
            </article>
            <article className="leaf-card">
              <div className="leaf-card-icon">
                <LeafMark size={24} fillColor="#B08D57" />
              </div>
              <h3>Pricing in the open.</h3>
              <p>
                Per-square-foot, published above. Same rate, same home, every visit.
                No surprise deep-clean upgrade three months in. The math doesn&rsquo;t change.
              </p>
            </article>
          </div>

          {/* Mid-chapter CTA */}
          <div className="chapter-cta-strip">
            <div className="chapter-cta-strip-copy">
              <h3>Ready to meet your crew?</h3>
              <p>Name and number. We&rsquo;ll be back within 10 minutes.</p>
            </div>
            <QuickLeadForm source="chapter-promise" label="Get started →" />
          </div>
        </div>
      </section>

      {/* ── HOW WE PAY ───────────────────────────────────── */}
      <section className="section-sm dark-section">
        <div className="dark-section-inner">
          <span className="chapter-label">Chapter three · how we pay</span>
          <h2 className="display-lg">
            W-2 only. No exceptions. Ever.
          </h2>
          <p className="lede">
            Every cleaning technician at New Leaf is a W-2 employee: paid, bonded, insured,
            background-checked. No gig apps. No 1099 contractors. No &ldquo;independent
            cleaner we sent your way.&rdquo;
          </p>
          <p className="lede">
            We do it this way because a foundation of trust matters. We&rsquo;re building a
            company that feels like a community &mdash; kindness, compassion, trust and
            respect, between us and toward the homes we&rsquo;re let into. You can&rsquo;t
            ask that of someone you treat as disposable.
          </p>
          <div className="stat-cards">
            <div className="stat-card">
              <div className="stat-number">$22&ndash;27</div>
              <div className="eyebrow stat-label">Effective hourly</div>
              <div className="stat-meta">Trainers earn $27/hr flat</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">W-2</div>
              <div className="eyebrow stat-label">Employment model</div>
              <div className="stat-meta">Licensed, bonded, insured</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">Same crew</div>
              <div className="eyebrow stat-label">Every visit</div>
              <div className="stat-meta">People who know your home</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CHAPTER III: THE MATH ────────────────────────── */}
      <section className="chapter chapter-alt">
        <div className="chapter-inner container">
          <div className="chapter-body-wide">
            <span className="chapter-label">Chapter four &middot; what it costs</span>
            <h2 className="display-lg">
              The whole price list.{' '}
              <em className="italic-display">Nothing under it.</em>
            </h2>
            <p className="lede chapter-lede">
              Every rate we charge is on this page. Work out your own number, or use
              the calculator at the top &mdash; either way you&rsquo;ll know before
              you ever speak to us.
            </p>
          </div>

          <div className="pricing-editorial">
            <div className="pricing-col">
              <div className="price-row">
                <div>
                  <div className="price-label">Weekly
                    <span className="price-sub">Most popular for families with young kids or pets.</span>
                  </div>
                </div>
                <div><span className="price-amount">{PRICING.weeklyRate.toFixed(2)}</span><span className="price-unit">/sqft</span></div>
              </div>
              <div className="price-row">
                <div>
                  <div className="price-label">Biweekly
                    <span className="price-sub">The rhythm most clients settle into.</span>
                  </div>
                </div>
                <div><span className="price-amount">{PRICING.biweeklyRate.toFixed(2)}</span><span className="price-unit">/sqft</span></div>
              </div>
              <div className="price-row">
                <div>
                  <div className="price-label">Every four weeks
                    <span className="price-sub">Low-maintenance homes, careful homeowners.</span>
                  </div>
                </div>
                <div><span className="price-amount">{PRICING.monthly4Rate.toFixed(2)}</span><span className="price-unit">/sqft</span></div>
              </div>
              <div className="price-row">
                <div>
                  <div className="price-label">First visit deep clean
                    <span className="price-sub">One-time add-on to your first recurring visit.</span>
                  </div>
                </div>
                <div><span className="price-amount">+${PRICING.deepUpgradeRate.toFixed(2)}</span><span className="price-unit">/sqft</span></div>
              </div>
            </div>

            <aside className="price-example">
              <p className="eyebrow">A real example</p>
              <h3>{exampleSqft.toLocaleString()} sqft Old Northeast bungalow, biweekly.</h3>
              <div className="price-ex-line">
                <span>{exampleSqft.toLocaleString()} sqft × ${PRICING.biweeklyRate.toFixed(2)}</span>
                <span>{formatPrice(examplePrice)}</span>
              </div>
              <div className="price-ex-line">
                <span>No contract, no setup, no add-on</span>
                <span>—</span>
              </div>
              <div className="price-ex-line">
                <span><strong>Every other Tuesday morning</strong></span>
                <span className="price-ex-total">{formatPrice(examplePrice)}</span>
              </div>
              <p className="marginalia price-example-marginalia">
                That&rsquo;s every penny. No upsell at the door.
              </p>
            </aside>
          </div>

          {/* Concrete absences beat a vague promise. Every line here is a fee a
              cleaning company could charge and we do not — checkable, and none
              of it is published anywhere else on the site. */}
          <div className="never-charge reveal">
            <h3>What we never charge for</h3>
            <ul>
              <li>Contracts or minimum terms &mdash; stop any time</li>
              <li>Sign-up, setup or cancellation fees</li>
              <li>Supplies and equipment &mdash; we bring our own</li>
              <li>Travel inside our service area</li>
              <li>Weekend or holiday premiums</li>
              <li>A surprise &ldquo;deep clean&rdquo; upsell later on</li>
            </ul>
          </div>

          <div className="chapter-body-wide">
            <p className="lede chapter-lede chapter-lede-mt">
              Two things worth knowing about the numbers above. The rate{' '}
              <strong>drops as the home gets bigger</strong> &mdash; past{' '}
              {PRICING.band1MaxSqft.toLocaleString()} sqft you pay a lower rate on
              the extra footage, because a large house isn&rsquo;t proportionally
              more work. And every job has a{' '}
              <strong>${PRICING.minimum} minimum</strong>, which is what makes a
              small home worth a real visit instead of a rushed one.
            </p>
            <p className="lede chapter-lede chapter-lede-mt">
              One last thing: <strong>10% of our profit</strong> goes back to the
              community we clean for &mdash; to St. Pete nonprofits our own crew
              picks.
            </p>
          </div>
        </div>
      </section>

      {/* ── CHAPTER IV: THE CREW ─────────────────────────── */}
      <section className="chapter">
        <div className="chapter-inner container">
          <div className="chapter-body-wide">
            <span className="chapter-label">Chapter five &middot; the people</span>
            <h2 className="display-lg">
              Our crew.{' '}
              <em className="italic-display">One standard.</em>
            </h2>
            <p className="lede chapter-lede">
              Not &ldquo;a technician from a pool of 80.&rdquo; A real employee, on a real
              payroll, held to the same written standard on every single visit.
            </p>
          </div>

          <div className="crew-standard">
            <div className="crew-standard-item">
              <span className="crew-standard-num">01</span>
              <h3 className="crew-standard-head">The same people</h3>
              <p>Your home is assigned to a crew, not to whoever is free that morning. They learn the house &mdash; which floors mark, which door sticks, where the dog hides.</p>
            </div>
            <div className="crew-standard-item">
              <span className="crew-standard-num">02</span>
              <h3 className="crew-standard-head">W&#8209;2, not gig</h3>
              <p>Every technician is a real employee: payroll taxes, workers&rsquo; comp, fair wages, a growth path. Nobody in your home is racing a quota.</p>
            </div>
            <div className="crew-standard-item">
              <span className="crew-standard-num">03</span>
              <h3 className="crew-standard-head">Screened and insured</h3>
              <p>Background checked before their first visit, and covered by our liability and bonding &mdash; so an accident is our problem, never yours.</p>
            </div>
            <div className="crew-standard-item">
              <span className="crew-standard-num">04</span>
              <h3 className="crew-standard-head">Trained to one checklist</h3>
              <p>We wrote our own training from scratch instead of buying a franchise binder. Every visit runs the same steps, in the same order, room by room.</p>
            </div>
          </div>

        </div>
      </section>

      {/* ── MID-PAGE CONTACT STRIP ───────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow contact-strip-eyebrow">Questions? Just want to talk?</p>
          <h2 className="display-sm contact-strip-heading">
            One human answers. No call centers.
          </h2>
          <p className="contact-strip-lede">
            Email <a href={`mailto:${SITE.contact.email}`} className="text-forest">{SITE.contact.email}</a>
            &nbsp;·&nbsp; {SITE.contact.hours}
          </p>
          <div className="contact-strip">
            <QuickLeadForm source="mid-page" label="Send us a message →" />
          </div>
        </div>
      </section>

      {/* ── W-2 EXPLAINER ────────────────────────────────── */}
      <section className="section-sm dark-section">
        <div className="dark-section-inner">
          <span className="eyebrow-sage">Why W-2 matters</span>
          <h2 className="display-sm w2-explainer-heading">
            Most cleaning companies use contractors. We don&rsquo;t.
          </h2>
          <p className="lede-sage w2-explainer-lede">
            When your cleaner is a contractor, nobody can require training, mandate
            background checks, or guarantee they&rsquo;ll show up. A W-2 employee is
            accountable. We can enforce standards, build a consistent crew, and stand
            behind every visit. It costs more. It&rsquo;s worth it.
          </p>
          <div className="section-cta">
            <Link to="/about" className="btn btn-ghost w2-explainer-cta">How we&rsquo;re built →</Link>
          </div>
        </div>
      </section>

      {/* The Chapter V testimonial wall lived here. Reviews moved to the top
          of the page as a scrolling band (ReviewMarquee, under the hero), so a
          second full section of the same three quotes was redundant. The full
          set still lives on /reviews. */}

      {/* ── CTA FINAL ────────────────────────────────────── */}
      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Begin</p>
          <h2>Your home, <em>on its own terms</em>.</h2>
          <p className="cta-lede">
            Breathing life back into your home, one clean at a time.
          </p>
          <Link to="/quote" className="btn btn-brass btn-lg">See My St. Pete Price →</Link>
          <span className="cta-closing">The whole thing takes ninety seconds.</span>
        </div>
      </section>
    </main>
  );
}
